import { sendMfaCode, verifyMfaCode } from '@iheartjane/data-access';
import { MfaProvider } from '@iheartjane/menu';
import type { QueryClient } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import type { RouteObject } from 'react-router';

import { getJwt } from '@jane/shared/auth';

import { Layout } from '../components';
import {
  cartConfirmationLoader,
  productDetailLoader,
  useKioskUser,
} from '../data-access';
import { AddMultiWeightPrompt } from '../pages/AddMultiWeightPrompt/AddMultiWeightPrompt';
import { Cart } from '../pages/Cart/Cart';
import { CartRemovalConfirmation } from '../pages/CartRemovalConfirmation/CartRemovalConfirmation';
import { Checkout } from '../pages/Checkout/Checkout';
import { CheckoutOnDevice } from '../pages/CheckoutOnDevice';
import { LinkBank } from '../pages/LinkBank/LinkBank';
import { Login } from '../pages/Login/Login';
import { Logout } from '../pages/Logout/Logout';
import { ProductDetail } from '../pages/ProductDetail/ProductDetail';
import { TermsOfService } from '../pages/TermsOfService/TermsOfService';
import { CheckoutProvider, ProductDetailsProvider } from '../providers';

const KioskMfaProvider = ({ children }: PropsWithChildren) => {
  const { data: kioskUser } = useKioskUser();
  return (
    <MfaProvider
      getJwt={getJwt}
      isKiosk
      sendMfaCode={sendMfaCode}
      userId={kioskUser?.id}
      verifyMfaCode={verifyMfaCode}
    >
      {children}
    </MfaProvider>
  );
};

const addMultiWeightPromptRoute = (queryClient: QueryClient) => ({
  children: [
    {
      element: <AddMultiWeightPrompt />,
      index: true,
    },
  ],
  element: <Cart />,
  id: 'cartWeightModal',
  loader: productDetailLoader(queryClient),
  path: 'select-addon-weight/:productId',
});

const cartConfirmationRoute = (queryClient: QueryClient) => ({
  children: [
    {
      element: <CartRemovalConfirmation />,
      index: true,
    },
  ],
  element: <Cart />,
  id: 'cartConfirmation',
  loader: cartConfirmationLoader(queryClient),
  path: 'remove/:productId',
});

const cartCheckoutRoute: RouteObject = {
  children: [
    {
      element: (
        <KioskMfaProvider>
          <CheckoutProvider>
            <Checkout />
          </CheckoutProvider>
        </KioskMfaProvider>
      ),
      index: true,
    },
  ],
  element: <Cart />,
  path: 'checkout',
};

const checkoutOnDeviceRoute: RouteObject = {
  children: [
    {
      element: <CheckoutOnDevice />,
      index: true,
    },
  ],
  element: <Cart />,
  path: 'checkout-on-device',
};

const cartProductDetailsRoute = () => ({
  children: [
    {
      element: (
        <ProductDetailsProvider>
          <ProductDetail />
        </ProductDetailsProvider>
      ),
      index: true,
    },
  ],
  element: <Cart />,
  id: 'cartPdp',
  path: `products/:productId`,
});

const cartLoginRoute: RouteObject = {
  children: [
    {
      element: <Login fromCart />,
      index: true,
    },
  ],
  element: <Cart />,
  path: 'login',
};

const cartLogoutRoute: RouteObject = {
  children: [
    {
      element: <Logout />,
      index: true,
    },
  ],
  element: <Cart />,
  path: 'logout',
};

const cartLinkBankRoute: RouteObject = {
  children: [
    {
      element: <LinkBank />,
      index: true,
    },
  ],
  element: <Cart />,
  path: 'link-bank',
};

const cartTermsRoute: RouteObject = {
  children: [
    {
      element: <TermsOfService />,
      index: true,
    },
  ],
  element: <Cart />,
  path: 'terms',
};

export const cartRoutes = (queryClient: QueryClient): RouteObject => {
  return {
    children: [
      {
        element: <Cart />,
        index: true,
      },
      addMultiWeightPromptRoute(queryClient),
      cartConfirmationRoute(queryClient),
      cartCheckoutRoute,
      checkoutOnDeviceRoute,
      cartProductDetailsRoute(),
      cartLoginRoute,
      cartLogoutRoute,
      cartLinkBankRoute,
      cartTermsRoute,
    ],
    element: <Layout />,
    path: 'cart',
  };
};
